import React, { useState, useEffect, useRef } from 'react';
import Modal from '../../ui/Modal';
import Button from '../../ui/Button';
import { FaEnvelope, FaPaperPlane, FaTimes } from 'react-icons/fa';
import { Loader2 } from 'lucide-react';

// Esta interfaz nos ayudará a manejar los datos de forma ordenada
export interface InvoiceRecipientInfo {
  recipientEmail: string; 
  customerName?: string;
  customerDocument?: string;
}

interface SendInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InvoiceRecipientInfo) => void;
  isSending: boolean;
}

const SendInvoiceModal: React.FC<SendInvoiceModalProps> = ({ isOpen, onClose, onSubmit, isSending }) => {
  const [email, setEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerDocument, setCustomerDocument] = useState('');
  const [showCustomerFields, setShowCustomerFields] = useState(false);
  const [error, setError] = useState('');
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => emailInputRef.current?.focus(), 100);
    } else {
      // Limpiar todo el estado cuando el modal se cierra
      setEmail('');
      setCustomerName('');
      setCustomerDocument('');
      setShowCustomerFields(false);
      setError('');
    }
  }, [isOpen]);

  const validateAndSubmit = () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Por favor, introduce un correo electrónico válido.');
      return;
    }
    
    if (showCustomerFields) {
      if (!customerName.trim()) {
        setError('El nombre del cliente es obligatorio si la casilla está marcada.');
        return;
      }
      if (!customerDocument.trim()) {
        setError('El documento del cliente es obligatorio si la casilla está marcada.');
        return;
      }
    }
    
    setError('');
    onSubmit({
      recipientEmail: email,
      customerName: showCustomerFields ? customerName : undefined,
      customerDocument: showCustomerFields ? customerDocument : undefined,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    validateAndSubmit();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Enviar Factura por Correo"
      size="md" // Un poco más grande para los campos extra
    >
      <form onSubmit={handleSubmit}>
        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="email-input" className="block text-sm font-medium text-gray-700 mb-1">
              Correo electrónico del destinatario <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaEnvelope className="text-gray-400" />
              </div>
              <input
                ref={emailInputRef}
                id="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ejemplo@correo.com"
                className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSending}
                required
              />
            </div>
          </div>

          <div className="relative flex items-start">
            <div className="flex items-center h-5">
              <input
                id="show-customer-fields"
                type="checkbox"
                checked={showCustomerFields}
                onChange={(e) => setShowCustomerFields(e.target.checked)}
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="show-customer-fields" className="font-medium text-gray-700 cursor-pointer">
                Asignar datos del cliente (Opcional)
              </label>
            </div>
          </div>

          {showCustomerFields && (
            <div className="p-4 border bg-gray-50 rounded-md space-y-4">
              <div>
                <label htmlFor="customer-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre completo <span className="text-red-500">*</span>
                </label>
                <input
                  id="customer-name"
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isSending}
                  placeholder="John Medina"
                />
              </div>
              <div>
                <label htmlFor="customer-document" className="block text-sm font-medium text-gray-700 mb-1">
                  Documento (C.C. / NIT) <span className="text-red-500">*</span>
                </label>
                <input
                  id="customer-document"
                  type="text"
                  value={customerDocument}
                  onChange={(e) => setCustomerDocument(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isSending}
                  placeholder="1234567890"
                />
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="bg-gray-50 px-6 py-4 flex justify-end items-center gap-3 rounded-b-md">
          <Button type="button" variant="secondary" icon={<FaTimes />} onClick={onClose} disabled={isSending}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" icon={isSending ? <Loader2 className="animate-spin" /> : <FaPaperPlane />} disabled={isSending || !email}>
            {isSending ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SendInvoiceModal;